import { Button, Checkbox, Label } from "@/components/ui";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useCreateOwnerMutation, useMergeOwnerMutation, useOwnerDuplicatesMutation } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Save, UserPlus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";
import EditOwner from "../OwnerDetails/EditOwner/EditOwner";
import { DEFAULT_COUNTRY, EDIT_OWNER_FORM_ID } from "../OwnerDetails/EditOwner/useEditOwner";
import { createOwnerSchema } from "../OwnerDetails/EditOwner/validations";

const ADDRESS_FIELDS = ["addressLine1", "addressLine2", "pobox", "city", "state", "zipCode", "country"];
const emptyAddress = () => Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, f === "country" ? DEFAULT_COUNTRY : ""]));
const blankToNull = (v: any) => (typeof v === "string" ? (v.trim() === "" ? null : v.trim()) : v ?? null);
const errorToast = (error?: any) =>
  toast.error(
    typeof error?.response?.data?.message === "string" ? error.response.data.message : "Something went wrong"
  );

/**
 * Users "+ Add User" (admin only, for exceptions): create the person as an owner (title, names, optional
 * email, mobile, landline, primary and correspondence address). User ID / Login ID are generated; the
 * property is attached later in Property Ownership. While typing, existing users matching on email, mobile
 * or name + address are shown; "Use Existing User" links that user to the community instead of creating a
 * duplicate. Opens the user's detail page after save.
 */
const CreateOwner = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAdmin = location.pathname.startsWith("/admin");
  const stateList = useSelector((state: RootState) => state.currentData.stateData) || [];
  const stateOptions = useMemo(() => stateList.map((s: any) => ({ label: s.name, value: s.code })), [stateList]);
  const toStateName = (v?: string) => stateList.find((s: any) => s.code == v)?.name || v || null;

  // Community comes from the Users grid selection (read-only); the owner is listed there until a
  // property is attached
  const community = useSelector((state: RootState) => state.currentData.communityData);
  const communityId = community?.communityId && community.communityId != "all" ? community.communityId : null;

  const form = useForm<any>({
    resolver: zodResolver(createOwnerSchema),
    defaultValues: {
      title: "", firstName: "", lastName: "", email: "", email2: "", phoneNumber: "", landlineNumber: "",
      address: emptyAddress(), separateCorrespondence: false, correspondenceAddress: emptyAddress(),
    },
  });

  const listPath = location.pathname.replace(/\/create\/?$/, "");
  const openUser = (userId?: number) => {
    queryClient.invalidateQueries({ queryKey: ["owner-master"] });
    navigate(userId ? `${listPath}/${routes.details}/${userId}` : listPath);
  };

  const { mutate: createOwner, isPending } = useCreateOwnerMutation({
    onError: errorToast,
    onSuccess: (response: any) => {
      const owner = response?.data?.data;
      toast.success(`User ${owner?.userName || ""} created successfully`);
      openUser(owner?.userId);
    },
  });

  const { mutate: mergeOwner, isPending: isMerging } = useMergeOwnerMutation({
    onError: errorToast,
    onSuccess: (response: any) => {
      const user = response?.data?.data;
      toast.success(`Using existing user ${user?.userName || ""} for ${community?.name || "this community"}`);
      openUser(user?.userId);
    },
  });

  // ---- duplicate check while typing ----
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [confirmDifferent, setConfirmDifferent] = useState(false);
  const { mutate: checkDuplicates } = useOwnerDuplicatesMutation({
    onSuccess: (response: any) => setDuplicates(response?.data?.data || []),
  });
  const [email, phoneNumber, firstName, lastName, addressLine1, zipCode] = form.watch([
    "email", "phoneNumber", "firstName", "lastName", "address.addressLine1", "address.zipCode",
  ]);
  useEffect(() => {
    const digits = (phoneNumber || "").replace(/\D/g, "");
    const searchable = (email || "").includes("@") || digits.length >= 7 || (!!firstName?.trim() && !!addressLine1?.trim());
    if (!searchable) {
      setDuplicates([]);
      return;
    }
    const timer = setTimeout(() => checkDuplicates({
      data: { communityId, email, phoneNumber, firstName, lastName, address: { addressLine1, zipCode } },
    }), 500);
    return () => clearTimeout(timer);
  }, [email, phoneNumber, firstName, lastName, addressLine1, zipCode, communityId]);
  useEffect(() => setConfirmDifferent(false), [duplicates]);

  // Email / mobile belong to one person: only "Use Existing User". A name + address match can be overridden.
  const contactMatch = duplicates.some((d) => d.matchedEmail || d.matchedPhone);
  const saveBlocked = contactMatch || (duplicates.length > 0 && !confirmDifferent);

  const toAddress = (a: any) => ({
    ...Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, blankToNull(a?.[f])])),
    state: toStateName(a?.state),
  });

  const toPayload = (d: any) => ({
    communityId: Number(communityId),
    title: blankToNull(d.title),
    firstName: blankToNull(d.firstName),
    lastName: blankToNull(d.lastName),
    email: blankToNull(d.email),
    phoneNumber: blankToNull(d.phoneNumber),
    landlineNumber: blankToNull(d.landlineNumber),
    address: toAddress(d.address),
    correspondenceAddress: d.separateCorrespondence ? toAddress(d.correspondenceAddress) : null,
  });

  const onSubmit = (d: any) => {
    if (!communityId) {
      toast.error("Select a community on the Users page first.");
      return;
    }
    if (saveBlocked) {
      toast.error("Resolve the duplicate warning first.");
      return;
    }
    createOwner({ data: { ...toPayload(d), allowDuplicate: confirmDifferent } });
  };

  const useExisting = (userId: number) => {
    if (!communityId) {
      toast.error("Select a community on the Users page first.");
      return;
    }
    mergeOwner({ userId, data: toPayload(form.getValues()) });
  };

  const matchedOn = (d: any) =>
    [d.matchedEmail && "Email", d.matchedPhone && "Phone", d.matchedNameAddress && "Name + Address"].filter(Boolean).join(", ");

  if (!isAdmin) {
    return (
      <div className="p-4 md:p-6">
        <p className="text-sm text-red-600">Only an admin can add users.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" size="icon" onClick={() => navigate(listPath)} aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2"><UserPlus className="w-5 h-5" /> Add User</h2>
            <p className="text-sm text-gray-500">Create the user as an owner (exceptions only). Attach the property later in Property Ownership.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(listPath)} disabled={isPending || isMerging}><X className="w-4 h-4" /> Cancel</Button>
          <Button type="submit" form={EDIT_OWNER_FORM_ID} disabled={isPending || isMerging || !communityId || saveBlocked}><Save className="w-4 h-4" /> Save User</Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border-t-4 border-primary shadow-sm p-4 md:p-6 flex flex-col gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Community</p>
          {communityId ? (
            <p className="text-base font-semibold">{community?.name}</p>
          ) : (
            <p className="text-sm text-red-600">No community selected — go back and pick one on the Users page.</p>
          )}
          <p className="text-xs text-gray-500">The user is listed in this community's Users grid as Owner (Inactive) until a property is attached.</p>
        </div>
        <EditOwner form={form} onSubmit={onSubmit} stateOptions={stateOptions} mode="create" />

        {duplicates.length > 0 && (
          <div className="rounded-lg border border-amber-400 bg-amber-50 p-4 flex flex-col gap-3">
            <p className="flex items-center gap-2 font-semibold text-amber-800">
              <AlertTriangle className="w-5 h-5" /> Duplicate warning — existing user record found
            </p>
            {duplicates.map((d) => (
              <div key={d.userId} className="rounded-md border border-amber-200 bg-white/60 p-3 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm">
                  <p className="font-semibold text-amber-900">User ID {d.userId} · {[d.firstName, d.lastName].filter(Boolean).join(" ")}</p>
                  <p className="text-amber-800">
                    User Name: {d.userName}{d.email ? ` · ${d.email}` : ""}{d.phoneNumber ? ` · ${d.phoneNumber}` : ""}
                  </p>
                  {d.address ? <p className="text-amber-800">{d.address}</p> : null}
                  <p className="text-xs font-medium text-amber-700">
                    Matched on: {matchedOn(d)}{d.inCommunity ? " · already in this community" : ""}
                  </p>
                </div>
                <Button type="button" variant="outline" disabled={isMerging || isPending} onClick={() => useExisting(d.userId)}>
                  Use Existing User
                </Button>
              </div>
            ))}
            {contactMatch ? (
              <p className="text-xs text-amber-800">The email or mobile number already belongs to an existing user, so a new user can't be created with it. Use the existing user.</p>
            ) : (
              <div className="flex items-center gap-2">
                <Checkbox id="confirm-different" checked={confirmDifferent} onCheckedChange={(v) => setConfirmDifferent(Boolean(v))} />
                <Label htmlFor="confirm-different" className="text-sm text-amber-900">This is a different person — create a new user</Label>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateOwner;
